/**
 * nodeModel — per-node model/vendor derivation, shared by the graph and the
 * pipeline detail page.
 *
 * The model an agent runs on is chosen PER NODE, on the graph (each node that
 * runs a model carries a small "model" sub-node; clicking it picks the vendor +
 * model + key). This module is the ONE place that decides, from data alone,
 * what a node's model state IS — so the disc, the trigger gate and the
 * sub-agent check can never disagree.
 *
 * Storage (mirrors self-host exactly):
 *   • vendor + model → the workflow row's `nodeConfigOverrides[<nodeId>]`
 *     ({agent, model}); the runner folds them into the run's
 *     `config.models[<node>]` / `config.agents[<node>]`, which are the top of
 *     the engine's own resolution chains. The ENGINE is untouched.
 *   • the KEY → per VENDOR, never per node: the agent's own env (a snapshot
 *     the runtime reads FIRST) and, optionally, the project's shared
 *     credential.
 *
 * Nothing here names an agent, a slug or a template.
 */
import { dispatchedSlugsOf } from './dispatchMarker';

// The env vars that carry a vendor's credential on an agent. First entry is
// what we WRITE; the rest are recognised when reading an existing snapshot.
export const VENDOR_ENV_KEYS = {
  claude: ['CLAUDE_CODE_OAUTH_TOKEN', 'ANTHROPIC_API_KEY', 'CLAUDE_CODE_CREDENTIALS_JSON'],
  codex: ['CODEX_API_KEY', 'OPENAI_API_KEY'],
  gemini: ['GEMINI_API_KEY', 'GOOGLE_API_KEY'],
};

export const VENDOR_LABELS = { claude: 'Claude', codex: 'Codex', gemini: 'Gemini' };

/** The node's serialized config from the graph (may be absent). */
export const nodeCfgOf = (graph, id) => {
  const c = graph?.nodeConfigs?.[id];
  return c && typeof c === 'object' ? c : null;
};

/** The node's code, however the serializer shipped it. */
const nodeCodeOf = (cfg) => {
  const s = cfg?.sourceCode || cfg?.executeCode || cfg?.customCode || '';
  return typeof s === 'string' ? s : '';
};

/**
 * Does this node invoke a model at all? A node with a prompt does; so does a
 * custom-execute node whose code calls invokeAgent. A SUB-GRAPH DISPATCH node
 * does NOT — it runs another agent, and that agent owns its own nodes' models
 * (set on the child's own page).
 */
export function nodeRunsModel(cfg) {
  if (!cfg) return false;
  // The marker is a STRING or an ARRAY OF STRINGS (a roster node declares every
  // member it dispatches on ONE node) — ask the normalizer, never `typeof`.
  if (dispatchedSlugsOf(cfg.dispatchesWorkflow).length) return false;
  if (typeof cfg.prompt === 'string' && cfg.prompt) return true;
  return nodeCodeOf(cfg).indexOf('invokeAgent') !== -1;
}

// A custom-execute node can hand its own `model:` to invokeAgent (a cheap
// triage tier, say), and that beats the run's model — so the operator's pick
// would not take effect and the control must be read-only.
const INVOKE_MODEL_RE = /invokeAgent\s*\([\s\S]{0,400}?\bmodel\s*:/;
// …and when what it passes is a plain STRING LITERAL we can name it, so the
// disc says `haiku-4.5` instead of the useless "fixed". An expression
// (`resolveTriageModel(state)` — a fixed cheap TIER whose vendor follows the
// run) is unknowable from here, and we say so rather than guess.
const INVOKE_MODEL_LITERAL_RE = /invokeAgent\s*\([\s\S]{0,400}?\bmodel\s*:\s*['"]([^'"]{1,64})['"]/;

/**
 * Is this node's model FIXED BY THE TEMPLATE'S CODE?
 *
 * NOTE the `overrides` argument: the graph a detail page renders has the USER's
 * per-node overrides already MERGED into nodeConfigs (so the node editor shows
 * saved edits). Reading `cfg.agent` blind therefore reports the operator's OWN
 * pick as "fixed in code" — which renders the node locked and unclickable the
 * moment they set it. A vendor present in the override map came from the
 * operator and is not a lock.
 *
 * @returns {{vendor: string|null, model: boolean, modelName: string}|null}
 *          null when not fixed. `modelName` is '' unless the code names a
 *          literal model we can read off.
 */
export function nodeModelLock(cfg, override, nodeId, modelBlocks) {
  if (!cfg) return null;
  const userPickedVendor = typeof override?.agent === 'string' && override.agent.trim();
  const vendor = !userPickedVendor && typeof cfg.agent === 'string' && cfg.agent.trim()
    ? cfg.agent.trim()
    : null;
  let codeModel = false;
  let modelName = '';
  try {
    const code = nodeCodeOf(cfg);
    codeModel = INVOKE_MODEL_RE.test(code);
    if (codeModel) {
      const m = code.match(INVOKE_MODEL_LITERAL_RE);
      // 'auto' names nothing — it IS the inherit case, so it would be a lie on
      // a disc that claims the model is fixed.
      if (m && m[1] && m[1] !== 'auto') modelName = m[1];
      // A bare `model:` in the code is NOT a lock by itself. The engine's chain
      // puts the operator's per-node pin ABOVE options.model
      // (resolveInvocationModel), so a code node FORWARDING a runtime value
      // (`model: state?.model`) is overridable like any other node — a padlock
      // there denies a picker that would actually work. A lock is claimable
      // only when the code names a LITERAL, or the template DECLARES the pin as
      // data (fixedModelByVendor, joined by role) — the deliberate-tier cases.
      if (!modelName && !hasDeclaredFixedModel(modelBlocks, nodeId)) {
        codeModel = false;
      }
    }
  } catch { codeModel = false; }
  if (!vendor && !codeModel) return null;
  return { vendor, model: codeModel, modelName };
}

/**
 * The `models[]` block this node is named by, or null.
 *
 * Blocks bind to nodes by `role === nodeId` (never by slug or position), which
 * is why a fleet parent can carry its children's blocks harmlessly: a block
 * whose role names no node of THIS graph binds nothing.
 */
export function declaredModelBlock(modelBlocks, nodeId) {
  if (!Array.isArray(modelBlocks) || !nodeId) return null;
  return modelBlocks.find((b) => b && b.role === nodeId) || null;
}

/**
 * The per-vendor DEFAULT-MODEL map a block declares, or null.
 *
 * TWO SPELLINGS, ONE MEANING. `defaultModelByVendor` is the current one;
 * `fixedModelByVendor` is what every row deployed before this shipped carries,
 * and it is also still the WIRE key the marketplace normalizer emits (renaming
 * the wire key would change every template's `declarationHash` and pop an
 * "update available" banner on all 33 installed agents for a change none of
 * them consume). Declarations are append-only in spirit: both are read here,
 * forever, newest spelling first.
 */
export function declaredModelMap(block) {
  const m = block?.defaultModelByVendor || block?.fixedModelByVendor;
  return (m && typeof m === 'object' && Object.keys(m).length) ? m : null;
}

/**
 * Does the template declare a DEFAULT model for this node at all (any vendor)?
 * The vendor-specific alias is resolved later by declaredFixedModel — this
 * presence check is what makes a code `model:` a template-supplied default
 * rather than an unset node.
 */
export function hasDeclaredFixedModel(modelBlocks, nodeId) {
  return !!declaredModelMap(declaredModelBlock(modelBlocks, nodeId));
}

/**
 * A block that is DISPLAY-ONLY: it names an identity, it does not run a model.
 *
 * `byok:false` (no key to collect) AND `required:false` (no credential needed
 * at all) is the template's own declarative way of saying so — figma's
 * "Claude Code" row, whose node signs in with the claude BINARY and never
 * invokes a model. Such a disc stays read-only and is NEVER gated on a key.
 * This is the ONE generic signal; there is no per-agent branch.
 */
export function isIdentityBlock(block) {
  return !!block && block.byok === false && block.required === false;
}

/**
 * THE VENDOR A RUN OF THIS AGENT WILL ACTUALLY USE — the UI's mirror of the
 * server's `resolveRunVendor` (backend/src/services/agent-key.js):
 *
 *     pinnedAgentFromNodes(nodeConfigOverrides) || row.aiAgent
 *
 * ⚠️ THERE IS NO PROJECT DEFAULT IN THIS CHAIN, DELIBERATELY. The project's
 * `aiAgent`, the box's `AGENT_TYPE` and the registry sentinel were all DELETED
 * from the run's resolution (founder, 2026-08-21: "no more default") — a run
 * never uses a value nobody chose for THIS agent. While the disc kept consulting
 * the project default it was asserting one of three possibilities as fact: an
 * agent whose only node pin said `codex` rendered its declared-model disc as
 * `haiku-4.5` (the CLAUDE entry, via the project's claude default) while every
 * run of it asked for `gpt-4o-mini`. Measured live 2026-08-26 on MAGNUM.
 *
 * `agentVendor` is the row's own `aiAgent` — or, for a dispatched sub-agent
 * whose row declares none, the OWNING parent's resolved vendor, because an
 * in-process child runs inside the parent's container under the parent's
 * AGENT_TYPE (see PipelineDetailPage's effectiveAgentVendor).
 *
 * @returns {string} the vendor, or '' when NOTHING resolves one — and '' is an
 *   answer, not a gap: a run with no vendor is refused by the same gate.
 */
export function runVendorOf(overrides, agentVendor) {
  const ov = (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) ? overrides : null;
  if (ov) {
    // First in SORTED node order — byte-identical to pinnedAgentFromNodes, so
    // a row with two disagreeing pins resolves the same way here and there.
    for (const nodeName of Object.keys(ov).sort()) {
      const a = ov[nodeName] && ov[nodeName].agent;
      if (typeof a === 'string' && a.trim()) return a.trim();
    }
  }
  return (typeof agentVendor === 'string' && agentVendor.trim()) ? agentVendor.trim() : '';
}

/**
 * What the disc says when the template declares a per-vendor default and NO
 * vendor is resolved yet.
 *
 * It MUST NOT be a model id. The declaration is a RULE — "this node runs the
 * cheapest tier of whatever vendor this agent runs on" — and on a fresh deploy
 * nobody has chosen the vendor, so all three entries are equally hypothetical.
 * Printing one of them (which is what a padlocked `haiku-4.5` did) is the UI
 * asserting a possibility as fact. Say the policy; the tooltip enumerates the
 * whole map, which is real data, and the concrete id appears the moment a
 * vendor exists.
 */
export const TEMPLATE_DEFAULT_LABEL = 'template default';

/** This node's saved pick, as {agent?, model?} ({} when it inherits). */
export function nodeModelPick(override) {
  const out = {};
  if (typeof override?.agent === 'string' && override.agent.trim()) out.agent = override.agent.trim();
  if (typeof override?.model === 'string' && override.model.trim()) out.model = override.model.trim();
  return out;
}

/**
 * Does a credential exist for `vendor`?
 *
 * The AGENT'S OWN ENV is checked FIRST and is sufficient on its own — that is
 * what the RUNTIME does (agent env beats the project credential). Judging by
 * the project alone marks a node red the moment the page reloads after saving
 * a key to the agent, and again whenever someone removes a project credential
 * the agent had stopped depending on.
 *
 * @param {object} agentEnvKeys  {ENV_NAME: lastFour|true} — the agent's env
 * @param {object} projectSecrets the project's masked secrets
 * @returns {boolean} — true when unknown (never cry wolf)
 */
export function vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor) {
  if (!vendor) return true;
  if (agentEnvKeys) {
    const names = VENDOR_ENV_KEYS[vendor] || [];
    for (const n of names) if (agentEnvKeys[n]) return true;
  }
  // …and the agent's own env is the ONLY thing that counts (founder,
  // 2026-08-27: "user has to open it, see the populated project token, click
  // Save"). A project credential nobody attached to this node is NOT a key on
  // this node — judging by it lit a fresh deploy's nodes as "set" before
  // anyone opened a modal, which read as the platform taking a token behind
  // the operator's back. The modal still pre-fills the project key; Save
  // adopts it (PATCH /env/<slot> {fromProjectVendor}) and only then does the
  // node light up. `projectVendor`/`projectSecrets` stay in the signature for
  // the "nothing loaded yet" abstention and for callers that mirror self-host.
  void projectVendor;
  if (!projectSecrets && !agentEnvKeys) return true; // nothing loaded yet
  return false;
}

/** The project's (masked) credential for a vendor, or ''. */
export function projectSecretFor(vendor, secrets, projectVendor) {
  const s = secrets || {};
  const str = (x) => (typeof x === 'string' && x ? x : '');
  const isPrimary = vendor === projectVendor;
  if (vendor === 'codex') return str(s.codexApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  if (vendor === 'gemini') return str(s.geminiApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  return str(s.aiOAuthToken) || str(s.aiApiKey); // claude
}

/** The agent's own snapshot for a vendor: its lastFour, `true`, or ''. */
export function agentSnapshotFor(vendor, agentEnvKeys) {
  const names = VENDOR_ENV_KEYS[vendor] || [];
  for (const n of names) {
    const hit = agentEnvKeys?.[n];
    if (hit) return hit;
  }
  return '';
}

/**
 * The model a code-LOCKED node will actually run, when the template DECLARES it.
 *
 * A node whose code picks its own model usually picks a cheap TIER per vendor
 * (`FAST_TRIAGE_MODEL_BY_VENDOR[resolveRunVendor(state)]`), so the concrete
 * alias is unknowable from the graph: what a deployed row carries is
 * `customExecute.toString()` — the function's OWN text, not the module around
 * it, so the map and the resolver it calls are simply absent.
 *
 * We don't guess at it. The template already publishes the answer as DATA: a
 * `models[]` block with `fixedModelByVendor` (the SAME frozen map the node asks
 * the vendor for, so the two cannot drift). Join it to the node by `role`.
 *
 * @param {object[]} modelBlocks  marketplace.models
 * @returns {string} the alias, or '' when nothing is declared for this node
 */
export function declaredFixedModel(modelBlocks, nodeId, vendor) {
  if (!vendor) return '';
  const byVendor = declaredModelMap(declaredModelBlock(modelBlocks, nodeId));
  if (!byVendor) return '';
  const m = byVendor[vendor];
  return typeof m === 'string' && m && m !== 'auto' ? m : '';
}

/**
 * The whole declared map as one line — "Claude → haiku-4.5 · Codex → …".
 *
 * This is what the RULE looks like spelled out, and it is real wire data (no
 * guess), so it belongs in the tooltip of a disc whose vendor is not resolved:
 * the caption states the policy, the tooltip shows every branch of it.
 */
export function declaredModelSummary(modelBlocks, nodeId) {
  const byVendor = declaredModelMap(declaredModelBlock(modelBlocks, nodeId));
  if (!byVendor) return '';
  return Object.keys(byVendor)
    .filter((v) => typeof byVendor[v] === 'string' && byVendor[v] && byVendor[v] !== 'auto')
    .map((v) => `${VENDOR_LABELS[v] || v} → ${byVendor[v]}`)
    .join(' · ');
}

/**
 * One node's model STATE — the single source the disc, the trigger gate and
 * the sub-agent check all read.
 *
 *   ready   → picked, and a credential exists for the vendor it will run on.
 *   nokey   → picked, but nothing can authenticate it. The run WOULD fail.
 *   default → the TEMPLATE declares this node's model (a per-vendor map, or a
 *             literal in its code) and nobody has overridden it. NOT a lock:
 *             the engine ranks a per-node pick ABOVE the model a node passes
 *             to invokeAgent (agent-workflow strategy-registry
 *             resolveInvocationModel: pinnedModel || nodeModel || … ||
 *             options.model), so a picker here has always taken effect — the
 *             padlock was denying a control that already worked. Founder,
 *             2026-08-25: "maybe just unlock it let people select whatever
 *             they want".
 *   locked   → genuinely nothing to pick: either an identity row the template
 *             declares display-only (byok:false + required:false), or a node
 *             whose VENDOR the template pins with no model to choose.
 *   unset    → nothing picked and nothing declared.
 *
 * @returns {{kind:'locked'|'default'|'ready'|'nokey'|'unset', vendor:string,
 *           label:string, sub:string, missingKey?:boolean, needsVendor?:boolean}}
 */
export function nodeModelState({
  cfg, nodeId, override, agentEnvKeys, projectSecrets, projectVendor, modelBlocks,
  agentVendor, overrides,
}) {
  const lock = nodeModelLock(cfg, override, nodeId, modelBlocks);
  const block = declaredModelBlock(modelBlocks, nodeId);
  const pick = nodeModelPick(override);

  // THE OPERATOR'S OWN PICK OUTRANKS EVERYTHING, and it is read FIRST — that is
  // the whole of the unlock. It mirrors the engine, where a per-node pin sits
  // at the top of resolveInvocationModel's chain; showing the template's value
  // over a saved pick would report a model the run is not going to use.
  // A model-only pick still needs a vendor to judge the key by, so it falls
  // through to the template's pin and then to the run's own vendor.
  if (pick.agent || pick.model) {
    const vendor = pick.agent || lock?.vendor || runVendorOf(overrides, agentVendor) || '';
    // A VENDOR-ONLY pick still runs the template's declared model for THAT
    // vendor (declaredFixedModel is the same map resolveRunVendor reads), so
    // the disc names the model that will actually run — not the vendor's name,
    // which is one level too abstract to be "true right now".
    const label = pick.model
      || (vendor ? declaredFixedModel(modelBlocks, nodeId, vendor) : '')
      || VENDOR_LABELS[vendor] || vendor;
    if (!vendor) return { kind: 'unset', vendor: '', label: 'Set model', sub: '', needsVendor: true };
    if (!vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor)) {
      return { kind: 'nokey', vendor, label, sub: `no ${VENDOR_LABELS[vendor] || vendor} key` };
    }
    return { kind: 'ready', vendor, label, sub: '' };
  }

  if (lock) {
    // WHICH vendor this node actually runs on — the run's OWN chain, not a
    // display-only guess: the template's node-level pin, then whatever
    // `runVendorOf` resolves (node pins, then the row's aiAgent). A template
    // that fixes a MODEL usually fixes a cheap TIER, not a vendor
    // (`PM_MODEL_BY_VENDOR[resolveRunVendor(state)]`), so the vendor is still
    // the operator's, and the disc must name the one that will really be used.
    // See runVendorOf for why the project default is NOT in this chain.
    const vendor = lock.vendor || runVendorOf(overrides, agentVendor) || '';

    // DISPLAY-ONLY identity row (byok:false + required:false): the node runs no
    // model and needs no credential, so there is nothing to pick and nothing to
    // go red about. Declarative — the block says so, no per-agent branch.
    if (isIdentityBlock(block)) {
      return {
        kind: 'locked',
        vendor,
        label: lock.modelName || declaredFixedModel(modelBlocks, nodeId, vendor)
          || VENDOR_LABELS[vendor] || vendor || 'Fixed',
        sub: '',
        missingKey: false,
      };
    }

    // A TEMPLATE-SUPPLIED MODEL — a declared per-vendor map or a literal in the
    // node's code. This is a DEFAULT the operator may change, not a lock.
    if (lock.model) {
      // No vendor resolved ⇒ say the RULE, never one of its three branches.
      // A run in this state is refused by the same gate (agent-key: "No AI
      // vendor resolved"), so the node genuinely is not ready, and it is now
      // clickable — the fix is one click from the complaint.
      if (!vendor) {
        return {
          kind: 'default',
          vendor: '',
          label: TEMPLATE_DEFAULT_LABEL,
          sub: 'pick a vendor',
          needsVendor: true,
        };
      }
      const declared = lock.modelName || declaredFixedModel(modelBlocks, nodeId, vendor);
      const missingKey = !vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor);
      return {
        kind: 'default',
        vendor,
        label: declared || TEMPLATE_DEFAULT_LABEL,
        sub: missingKey ? `no ${VENDOR_LABELS[vendor] || vendor} key` : '',
        missingKey,
      };
    }

    // A template VENDOR pin with no model to choose. Unchanged: the vendor is
    // the template's, the KEY is still the operator's to supply, so a missing
    // credential is as dead as an unset node and says so.
    const missingKey = !vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor);
    return {
      kind: 'locked',
      vendor,
      label: VENDOR_LABELS[vendor] || vendor || 'Fixed',
      sub: missingKey ? `no ${VENDOR_LABELS[vendor] || vendor} key` : '',
      missingKey,
    };
  }
  return { kind: 'unset', vendor: '', label: 'Set model', sub: '' };
}

/**
 * Nodes of this graph whose model is still UNSET — model-running, no template
 * default, no saved pick. These are the "apply the same model to all"
 * candidates: a node that already carries a pick is never listed (never
 * overwritten), a node the TEMPLATE supplies a model for is never listed,
 *
 * ⚠️ AND THAT LAST EXCLUSION SURVIVED THE UNLOCK, ON PURPOSE — twice over.
 * (1) BACKWARD COMPATIBILITY: the server's `declaredFixedRunModel` only
 * resolves a template's declared model when this list is EMPTY, so listing a
 * declared-default node here would make every already-deployed agent whose
 * only model node is declared (MAGNUM) refuse to run until someone picked —
 * a behaviour change for an agent nobody touched. (2) INTENT: "apply this
 * model to all" would overwrite a deliberately cheap routing tier with the
 * operator's top-tier pick for the WORK, which is the opposite of what the
 * declaration is for. Unlocked means "you MAY change it", not "it is unset".
 *
 * A locked node (a template vendor pin) is never listed (never touched),
 * and a node whose declared block is an EMBEDDING is excluded — its pick is
 * agent state (embeddingModel), not a node override, so a chat {agent, model}
 * written there would claim an LLM pick on a node that runs none.
 *
 * @param {string} [excludeId] the node whose save triggered the question —
 *        it just received its pick, so it is neither "other" nor unset.
 */
export function unsetModelNodes({ graph, overrides, modelBlocks, excludeId = null }) {
  const out = [];
  for (const n of (graph?.nodes || [])) {
    if (!n || !n.id || n.id === excludeId) continue;
    const cfg = nodeCfgOf(graph, n.id);
    if (!nodeRunsModel(cfg)) continue;
    const block = Array.isArray(modelBlocks)
      ? modelBlocks.find((b) => b && b.role === n.id)
      : null;
    if (block?.kind === 'embedding') continue;
    // A declaration supplies this node's model for every node shape, including
    // ordinary prompt nodes. It is therefore not an "unset" target for a bulk
    // model pick, even though the detail disc may present the template default
    // without a padlock.
    if (declaredModelMap(block)) continue;
    const override = overrides?.[n.id];
    if (nodeModelLock(cfg, override, n.id, modelBlocks)) continue;
    const pick = nodeModelPick(override);
    if (!pick.agent && !pick.model) out.push(n.id);
  }
  return out;
}

/**
 * Nodes of this graph that cannot run yet: no model picked, or a model whose
 * vendor has no credential anywhere.
 *
 * A node the template supplies a model for is NOT ready by construction — the
 * template chose which model runs, not who pays for it, so one whose vendor has
 * no key fails the run exactly like an unset node. Skipping them here is what
 * made such a node a dead end: unclickable AND never flagged.
 *
 * AND A NODE WHOSE VENDOR NOTHING RESOLVES IS NOT READY EITHER. That was the
 * second half of the founder's report (2026-08-25: "I didn't even give it any
 * claude code token why the agent let me click trigger??"): with no vendor,
 * `vendorHasKey('')` answers `true` — "never cry wolf" — so the node passed
 * this check while the server refused the very same run for having no vendor to
 * resolve. `needsVendor` closes that, through the SAME gate and the same amber
 * ring; nothing new was added to decide it.
 */
export function unreadyNodes({
  graph, overrides, agentEnvKeys, projectSecrets, projectVendor, modelBlocks,
  agentVendor,
}) {
  const out = [];
  for (const n of (graph?.nodes || [])) {
    const cfg = nodeCfgOf(graph, n.id);
    if (!nodeRunsModel(cfg)) continue;
    const st = nodeModelState({
      cfg,
      nodeId: n.id,
      override: overrides?.[n.id],
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      agentVendor,
      overrides,
    });
    if (st.kind === 'unset' || st.needsVendor) out.push({ id: n.id, why: 'no model' });
    else if (st.kind === 'nokey' || st.missingKey) out.push({ id: n.id, why: 'no key' });
  }
  return out;
}
